import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Row, Col, Card, Tag, Button, Progress, Avatar, Breadcrumb, Divider } from 'antd';
import { toast } from '../../../utils/toast.js';
import {
  BookOutlined,
  ClockCircleOutlined,
  UserOutlined,
  PlayCircleFilled,
  FormOutlined,
  TrophyOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import { courseApi } from '../../../api/course.api.js';
import { learningApi } from '../../../api/learning.api.js';
import { useAuth } from '../../../contexts/AuthContext.jsx';
import { SyllabusList } from '../components/SyllabusList.jsx';
import { COURSE_THUMBNAILS } from '../components/CourseCard.jsx';
import { LoadingSpinner } from '../../../components/common/LoadingSpinner.jsx';
import { ErrorState } from '../../../components/common/ErrorState.jsx';
import '../styles/course-detail.css';

export function CourseDetailPage() {
  const { idOrSlug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [error, setError] = useState(null);
  const { isAuthenticated, canManageContent } = useAuth();
  const navigate = useNavigate();

  const fetchCourse = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await courseApi.getCourseDetails(idOrSlug);
      setData(res.data);
    } catch (err) {
      setError(err.message || 'Không thể tải thông tin khóa học');
    } finally {
      setLoading(false);
    }
  }, [idOrSlug]);

  useEffect(() => {
    fetchCourse();
  }, [fetchCourse]);

  if (loading) {
    return <LoadingSpinner tip="Đang tải thông tin khóa học..." minHeight="400px" />;
  }

  if (error || !data?.course) {
    return <ErrorState title="Không tìm thấy khóa học" subTitle={error} onRetry={fetchCourse} />;
  }

  const { course, syllabus = [], quiz, isEnrolled, progress } = data;

  // Tìm bài học đầu tiên hoặc bài học đang học dở dang
  let targetLessonId = null;
  if (syllabus.length > 0 && syllabus[0].lessons?.length > 0) {
    targetLessonId = syllabus[0].lessons[0].id;
  }
  if (progress?.lastAccessedLessonId) {
    targetLessonId = progress.lastAccessedLessonId;
  }

  const handleStartLearning = async () => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: { pathname: `/courses/${idOrSlug}` } } });
      return;
    }

    setEnrolling(true);
    try {
      if (!isEnrolled) {
        await learningApi.enrollCourse(course.id);
        toast.success('Đã ghi danh khóa học thành công!');
      }
      if (targetLessonId) {
        navigate(`/learn/${course.id}/lessons/${targetLessonId}`);
      }
    } catch (err) {
      toast.error(err.message || 'Lỗi khi ghi danh khóa học');
    } finally {
      setEnrolling(false);
    }
  };

  return (
    <div className="main-container">
      {/* Breadcrumb */}
      <Breadcrumb
        style={{ marginBottom: 20 }}
        items={[
          ...(canManageContent ? [{ title: <Link to="/admin">Trang Quản Trị</Link> }] : []),
          { title: <Link to="/courses?tab=topics">Khóa Học</Link> },
          {
            title: course.category ? (
              <Link to={`/courses?tab=topics&categoryId=${course.category.id}`}>
                {course.category.name}
              </Link>
            ) : (
              'Chuyên đề'
            ),
          },
          { title: course.title },
        ]}
      />

      <Row gutter={[28, 28]}>
        {/* Left Column: Course Details & Syllabus */}
        <Col xs={{ span: 24, order: 2 }} lg={{ span: 16, order: 1 }}>
          {/* Header Card */}
          <div
            className="course-detail-head"
            style={{
              background: '#ffffff',
              borderRadius: 10,
              padding: '28px 24px',
              border: '1px solid #e2e8f0',
              marginBottom: 24,
            }}
          >
            <div style={{ marginBottom: 12 }}>
              <Tag color={course.category?.color || '#0284c7'} style={{ fontWeight: 600 }}>
                {course.category?.name}
              </Tag>
              <Tag color="blue">{course.level === 'ALL' ? 'Mọi cấp độ' : course.level}</Tag>
            </div>

            <h1 className="course-detail-title" style={{ fontSize: 24, fontWeight: 800, color: '#0f172a', lineHeight: 1.35, marginBottom: 12 }}>
              {course.title}
            </h1>

            <p className="course-detail-desc" style={{ fontSize: 15, color: '#475569', lineHeight: 1.6, marginBottom: 20 }}>
              {course.shortDescription}
            </p>

            {/* Instructor Info */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '12px 16px',
                background: '#f8fafc',
                borderRadius: 8,
                border: '1px solid #f1f5f9',
              }}
            >
              <Avatar
                src={course.instructor?.avatar || '/images/the-thao-pharma-logo.jpg'}
                icon={!course.instructor?.avatar && <UserOutlined />}
                size={46}
                style={{ backgroundColor: '#005537' }}
              />
              <div>
                <div style={{ fontWeight: 700, fontSize: 14.5, color: '#1e293b' }}>
                  {course.instructor?.fullName || 'Thế Thảo Pharma'}
                </div>
                <div style={{ fontSize: 12.5, color: '#64748b', marginTop: 2 }}>
                  {course.instructor?.position || 'Đồng hành cùng nhà thuốc vươn xa'}
                </div>
              </div>
            </div>
          </div>


          {/* Syllabus Accordion */}
          <div style={{ marginBottom: 24 }}>
            <div className="course-syllabus-heading" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
              <h2 style={{ fontSize: 18, fontWeight: 700, color: '#0f172a', margin: 0 }}>
                📖 Nội Dung Giáo Trình Đào Tạo
              </h2>
              <span style={{ fontSize: 13, color: '#64748b' }}>
                {syllabus.length} chương • {course.totalLessons} bài học
              </span>
            </div>

            <SyllabusList
              syllabus={syllabus}
              courseId={course.id}
              courseInstructorId={course.instructorId}
              completedLessonIds={progress?.completedLessonIds || []}
              isEnrolled={isEnrolled}
              onRefresh={fetchCourse}
            />
          </div>

          {/* Quiz Section if exists */}
          {quiz && (
            <Card
              className="course-quiz-card"
              style={{
                borderRadius: 10,
                border: '1px solid #fef3c7',
                background: '#fffbeb',
                marginBottom: 24,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <FormOutlined style={{ fontSize: 24, color: '#d97706' }} />
                  <div>
                    <h4 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#92400e' }}>
                      {quiz.title}
                    </h4>
                    <p style={{ margin: '2px 0 0', fontSize: 13, color: '#b45309' }}>
                      Đạt khi trên 5/10 điểm. Mỗi module phải đạt bài test thì module sau trong chủ đề mới mở
                    </p>
                  </div>
                </div>

                <Button
                  type="primary"
                  style={{ background: '#d97706', borderColor: '#d97706' }}
                  onClick={() => navigate(`/quiz/${quiz.id}`)}
                >
                  Làm Bài Test
                </Button>
              </div>
            </Card>
          )}
        </Col>

        {/* Right Column: Sticky Action Card (điện thoại: hiện trước giáo trình) */}
        <Col xs={{ span: 24, order: 1 }} lg={{ span: 8, order: 2 }}>
          <div className="course-action-sticky" style={{ position: 'sticky', top: 88 }}>
            <Card
              style={{ borderRadius: 10, overflow: 'hidden', border: '1px solid #e2e8f0' }}
              cover={
                <img
                  alt={course.title}
                  src={COURSE_THUMBNAILS[course.slug] || course.thumbnail || '/images/courses/anh_module_sale.png'}
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = '/images/courses/anh_module_sale.png';
                  }}
                  style={{ height: 200, objectFit: 'cover' }}
                />
              }
            >
              {/* Progress if Enrolled */}
              {isEnrolled && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6 }}>
                    <span style={{ fontWeight: 600, color: '#1e293b' }}>Tiến độ học tập</span>
                    <span style={{ fontWeight: 700, color: '#0284c7' }}>
                      {progress?.completionPercentage || 0}%
                    </span>
                  </div>
                  <Progress percent={progress?.completionPercentage || 0} showInfo={false} strokeColor="#0284c7" />
                  {progress?.isCompleted && (
                    <Tag color="green" icon={<TrophyOutlined />} style={{ marginTop: 8, width: '100%', textAlign: 'center', padding: '4px 0' }}>
                      Đã hoàn thành xuất sắc!
                    </Tag>
                  )}
                </div>
              )}

              {/* Action Button */}
              <Button
                type="primary"
                size="large"
                block
                icon={<PlayCircleFilled />}
                loading={enrolling}
                onClick={handleStartLearning}
                style={{ height: 46, fontSize: 15, fontWeight: 700, marginBottom: 16 }}
              >
                {isEnrolled ? (progress?.completionPercentage > 0 ? 'Tiếp Tục Học' : 'Bắt Đầu Học') : 'Bắt Đầu Học Ngay'}
              </Button>

              <Divider style={{ margin: '14px 0' }} />

              {/* Course Features */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 13, color: '#475569' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>
                    <BookOutlined /> Tổng số bài học:
                  </span>
                  <strong>{course.totalLessons} bài</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>
                    <ClockCircleOutlined /> Thời lượng ước tính:
                  </span>
                  <strong>{course.estimatedHours} giờ</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>
                    <SafetyCertificateOutlined /> Chứng chỉ:
                  </span>
                  <strong>Cấp khi đạt 100%</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>
                    <UserOutlined /> Hình thức:
                  </span>
                  <strong>Học trực tuyến 24/7</strong>
                </div>
              </div>
            </Card>
          </div>
        </Col>
      </Row>
    </div>
  );
}
